import { AxiosInstance } from "axios";
import { DirectoryType } from "./Types";

export interface DirectoryGroup {
    id: string
    name: string
    included: boolean
}

export interface DirectoryGroupsPage {
    groups: DirectoryGroup[]
    nextCursor?: string
    includedCount: number
    /**
     * How many groups the directory holds matching the current search.
     *
     * Absent means the provider cannot report one - not zero. LDAP and Azure
     * answer exactly; Google and Okta expose no count on their listing
     * endpoints, so nothing here may depend on it being present.
     */
    total?: number
    truncated?: boolean
    /**
     * The directory holds too many matching groups for its domain controller to
     * sort them, so no ordered page exists - not even the first. `groups` is
     * empty. Both `name` and `recent` can land here. Narrow with a search term,
     * or switch to `default` order, which asks for no ordering and so has no
     * such limit. LDAP only.
     */
    searchRequired?: boolean
}

/**
 * How a listing is ordered. These are not just display preferences: `name` and
 * `recent` ask the domain controller to sort, which it refuses above a policy
 * limit (see `searchRequired`), while `default` asks for no ordering and so has
 * no limit.
 *
 * `default` is named for being the one you get when you ask for none, not for
 * the order it produces, which is whatever order the directory streams back.
 * Do not describe it to a user as oldest-first: on the directories we have
 * measured it comes back that way, but nothing makes the server promise it.
 *
 * `recent` is a sort by creation time, NOT `default` reversed - not even where
 * that measured order does hold, since `default` is an unordered paged walk
 * that cannot be run backwards. Newest-first has to be asked for as a sort, and
 * pays the same policy limit as `name` for it.
 */
export type DirectoryGroupsSort = "name" | "default" | "recent"

/**
 * The ordering used when the user has not chosen one.
 *
 * Directory order rather than alphabetical because it is the only one that loads
 * on any directory: the sorted orderings come back `searchRequired` above the
 * DC's policy limit, which made the first page of a large directory empty until
 * a search term was typed.
 */
export const defaultDirectoryGroupsSort: DirectoryGroupsSort = "default";

/**
 * Which providers can actually honour a sort choice.
 *
 * Only LDAP implements it today. The others are listed here rather than left
 * implicit because each is blocked for a different, concrete reason:
 *
 *  - azure  — supports $orderby=displayName and createdDateTime, so it could be
 *             added; just not wired up yet.
 *  - okta   — sortBy is accepted only alongside a search term, so a sort choice
 *             would silently do nothing while browsing.
 *  - google — groups.list only sorts by EMAIL, which is not the name shown in
 *             this table, so "Name (A-Z)" could not be honoured at all.
 *
 * Showing the control where the backend ignores it is worse than not offering
 * it: the list visibly refetches and comes back in the same order.
 */
export const directorySupportsSorting: { [key in DirectoryType]: boolean } = {
    ldap: true,
    azure: false,
    okta: false,
    google: false,
    // Never actually shown - the Proxy Directory has no live provider to browse at all.
    proxy: false,
};

/** How a search term is matched against a group name. */
export type DirectoryGroupsNameMatch = "contains" | "prefix"

/**
 * A group's category. In Active Directory this is a bit of `groupType` rather
 * than a field, and a security group is the only kind an ACL - and so a policy -
 * can act on.
 */
export type DirectoryGroupsCategory = "any" | "security" | "distribution"

/** A group's scope, also carried in `groupType`. */
export type DirectoryGroupsScope = "any" | "global" | "universal" | "domain_local"

/**
 * The narrowings that decide which groups match, as opposed to how they are
 * ordered (`sort`) or paged (`cursor`).
 */
export interface DirectoryGroupFilters {
    nameMatch: DirectoryGroupsNameMatch
    category: DirectoryGroupsCategory
    scope: DirectoryGroupsScope
}

export const defaultDirectoryGroupFilters: DirectoryGroupFilters = {
    nameMatch: "contains",
    category: "any",
    scope: "any",
};

/**
 * Whether these filters narrow the listing to less than the whole directory.
 *
 * `nameMatch` is deliberately not counted: on its own it narrows nothing, and
 * when there is a search term to modify, that term already narrows the listing.
 */
export function hasActiveGroupFilters(filters: DirectoryGroupFilters): boolean {
    return filters.category !== "any" || filters.scope !== "any";
}

/**
 * Which providers can honour a category or scope filter.
 *
 * Only LDAP, and unlike sorting this is not a matter of wiring: category and
 * scope are Active Directory's own notions, carried in `groupType`. Azure, Okta
 * and Google groups have no equivalent to narrow by, so there is nothing to
 * offer rather than something not yet built.
 */
export const directorySupportsGroupFilters: { [key in DirectoryType]: boolean } = {
    ldap: true,
    azure: false,
    okta: false,
    google: false,
    proxy: false,
};

export const advancedFilterPrefix = "Get-ADGroup -Filter";

export const advancedFilterPlaceholder = "Name -like 'Sales*' -and GroupCategory -eq 'Security'";

export const directorySupportsAdvancedFilter: { [key in DirectoryType]: boolean } = {
    ldap: true,
    azure: false,
    okta: false,
    google: false,
    proxy: false,
};

/** Where in the expression a translation failed, so the UI can point at it. */
export interface GroupFilterErrorDetail {
    message: string
    offset: number
    token?: string
}

export interface GroupFilterTranslation {
    ldapFilter?: string
    error?: GroupFilterErrorDetail
}

export function filterErrorIndex(expression: string, byteOffset: number): number {
    if (byteOffset <= 0) {
        return 0;
    }
    const bytes = new TextEncoder().encode(expression);
    return new TextDecoder().decode(bytes.slice(0, byteOffset)).length;
}

export async function translateGroupFilter(
    req: AxiosInstance,
    expression: string,
): Promise<GroupFilterTranslation> {
    const response = await req.get<GroupFilterTranslation>("/api/directories/group-filter", {
        params: { expression },
    });
    return response.data;
}

const maxBulkPages = 1000;

export async function listDirectoryGroups(
    req: AxiosInstance,
    directoryId: string,
    cursor: string,
    limit: number,
    search: string,
    sort?: DirectoryGroupsSort,
    filters?: DirectoryGroupFilters,
    advanced?: string,
): Promise<DirectoryGroupsPage> {
    const matching = advanced !== undefined && advanced.trim() !== ""
        ? { advancedFilter: advanced }
        : { search, ...(filters ?? {}) };

    const response = await req.get<DirectoryGroupsPage>(`/api/directories/groups/${directoryId}`, {
        params: {
            cursor,
            limit,
            ...(sort ? { sort } : {}),
            ...matching,
        },
    });
    return response.data;
}

export async function saveDirectoryGroups(
    req: AxiosInstance,
    directoryId: string,
    changes: DirectoryGroup[],
    replaceWith?: boolean,
    sort?: DirectoryGroupsSort,
): Promise<void> {
    let groups = changes;
    if (replaceWith === true) {
        const overrides = new Map(changes.map((group) => [group.id, group.included]));
        groups = [];
        let cursor = "";
        const seenCursors = new Set<string>([cursor]);
        for (let pageNumber = 0; pageNumber < maxBulkPages; pageNumber += 1) {
            // Walked with no search and no filters, because a replacement covers
            // the whole directory: a narrowed walk would drop every group outside
            // it from the inclusion list. The caller is responsible for not
            // offering the replacement while a search or filter is active.
            const page = await listDirectoryGroups(req, directoryId, cursor, 200, "", sort);
            // The DC refused to sort this set, so the walk cannot see the groups it
            // is about to replace. Replacing on an empty walk would clear the whole
            // inclusion list.
            if (page.searchRequired === true) {
                throw new Error("This directory is too large to sort; switch to the directory order before replacing every group.");
            }
            // A bounded sample cannot stand in for the directory either, and it is
            // the quieter of the two: the walk runs to a clean end having never
            // seen the groups past the cap, so the replacement drops every one of
            // them with nothing to show that it did.
            if (page.truncated === true) {
                throw new Error("Only part of this directory can be listed at once, so replacing every group would drop the ones it cannot see. Scope this directory to fewer forest domains first.");
            }
            groups = groups.concat(page.groups
                .map((group) => ({ ...group, included: overrides.get(group.id) ?? true }))
                .filter((group) => group.included));
            const nextCursor = page.nextCursor || "";
            if (!nextCursor) {
                cursor = "";
                break;
            }
            if (seenCursors.has(nextCursor)) {
                throw new Error("Directory group pagination returned a repeated cursor.");
            }
            seenCursors.add(nextCursor);
            cursor = nextCursor;
        }
        if (cursor) {
            throw new Error(`Directory group pagination exceeded ${maxBulkPages} pages.`);
        }
    } else if (replaceWith === false) {
        groups = changes.filter((group) => group.included);
    }
    await req.put(`/api/directory-groups/${directoryId}`, {
        groups,
        replace: replaceWith !== undefined,
    });
}
